import { useEffect, useState } from "react"
import type { Course } from "@/modules/courses/entities/courses"
import CourseCard from "./subject-card"
import CourseCardSkeleton from "./subject-card-skeletons"
import TargetObjectiveModal from "../target-objetive-modal"
import { target } from "@/modules/courses/constants/courses"
import { useQueryClient } from "@tanstack/react-query"
import useCoursesInfo from "@/modules/courses/hooks/use-courses-info"

interface CourseCardsProps {
  activeCourses: Course[]
}

export default function CourseCards({ activeCourses }: CourseCardsProps) {
  const queryClient = useQueryClient()

  const { coursesInfo, hasCourses, refetch, isFetching, updateCourseTargetObjective } =
    useCoursesInfo()

  const [openModal, setOpenModal] = useState(false)
  const [selectedCourse, setSelectedCourse] = useState<Course>({
    id: "",
    name: "",
    remainingStudyTime: "",
    targetObjective: "approve",
    status: "on_track",
    endDate: new Date().toISOString(),
    startDate: new Date().toISOString(),
    subjectId: "",
  })

  useEffect(() => {
    if (hasCourses) {
      setSelectedCourse(coursesInfo.courses[0])
    }
  }, [hasCourses])

  function handleOpenModal(courseId: string) {
    const course = coursesInfo.courses.find((course) => course.id === courseId)!
    setSelectedCourse(course)
    setOpenModal(true)
  }

  function handleOnSave(value: Course["targetObjective"]) {
    updateCourseTargetObjective(selectedCourse.id, value)

    refetch()
    queryClient.refetchQueries({
      queryKey: ["planner-get-weekly-objectives"],
    })
    setOpenModal(false)
  }

  if (isFetching)
    return (
      <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <CourseCardSkeleton key={index} />
        ))}
      </div>
    )

  return (
    <div className="flex h-full w-full">
      <TargetObjectiveModal
        key={openModal ? "open" : "closed"}
        open={openModal}
        target={target}
        setOpen={setOpenModal}
        selectedCourse={selectedCourse}
        onSave={handleOnSave}
      />
      <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {coursesInfo.courses.map((course, index) => {
          const isActive = activeCourses.some((activeCourse) => activeCourse.id === course.id)

          return (
            <CourseCard
              index={index}
              key={course.id}
              target={target}
              course={course}
              openEditGradeModal={handleOpenModal}
              isActive={isActive}
            />
          )
        })}
      </div>
    </div>
  )
}
